<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue';
import { useHaptics } from '@/composables/useHaptics';

interface ScrubberSection {
  letter: string;
  elementId: string;
}

const props = defineProps<{
  sections: ScrubberSection[];
}>();

const emit = defineEmits<{
  navigate: [letter: string];
}>();

const activeLetter = ref<string | null>(null);
const isDragging = ref(false);
const dragLetter = ref<string | null>(null);
const { triggerVibration } = useHaptics();

let activeTimeout: ReturnType<typeof setTimeout> | null = null;

function handleLetterClick(letter: string) {
  emit('navigate', letter);
  triggerVibration();

  activeLetter.value = letter;
  if (activeTimeout) clearTimeout(activeTimeout);
  activeTimeout = setTimeout(() => {
    activeLetter.value = null;
  }, 600);
}

function getLetterFromTouch(e: TouchEvent): string | null {
  const touch = e.touches[0];
  if (!touch) return null;
  const el = document.elementFromPoint(touch.clientX, touch.clientY);
  return el?.getAttribute('data-letter') ?? null;
}

function onTouchStart(e: TouchEvent) {
  isDragging.value = true;
  const letter = getLetterFromTouch(e);
  if (letter) {
    dragLetter.value = letter;
    emit('navigate', letter);
    triggerVibration();
  }
}

function onTouchMove(e: TouchEvent) {
  e.preventDefault();
  const letter = getLetterFromTouch(e);
  if (letter && letter !== dragLetter.value) {
    dragLetter.value = letter;
    emit('navigate', letter);
    triggerVibration();
  }
}

function onTouchEnd() {
  isDragging.value = false;
  dragLetter.value = null;
}

onBeforeUnmount(() => {
  if (activeTimeout) clearTimeout(activeTimeout);
});
</script>

<template>
  <nav
    v-if="props.sections.length > 0"
    data-testid="section-scrubber"
    class="!visible fixed -right-1 top-1/2 z-[15] flex -translate-y-1/2 flex-col items-center gap-0.5 py-2 min-[600px]:right-1 min-[1280px]:hidden pointer-events-none"
    @touchstart.passive="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <Transition name="fade">
      <span
        v-if="isDragging && dragLetter"
        class="absolute right-8 flex h-12 w-12 items-center justify-center rounded-full bg-election-brand text-2xl font-bold text-on-election-brand shadow-lg"
      >
        {{ dragLetter }}
      </span>
    </Transition>

    <button
      v-for="section in props.sections"
      :key="section.letter"
      :data-letter="section.letter"
      data-testid="scrubber-letter"
      class="flex h-4 w-5 items-center justify-center text-[10px] font-bold leading-none transition-all duration-150 pointer-events-auto"
      :class="
        activeLetter === section.letter || dragLetter === section.letter
          ? 'scale-125 text-election-brand'
          : 'text-muted-color'
      "
      @click="handleLetterClick(section.letter)"
    >
      {{ section.letter }}
    </button>
  </nav>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}
</style>
<!-- last modified by AI tools on 2026-10-09 -->
